import * as Tabs from '@radix-ui/react-tabs'
import TokenGroupTable, { type TokenGroup } from './TokenGroupTable'

const ALL_SCALES = [
  { name: 'Gray',   dotVar: '--gray-9',   steps: Array.from({length: 12}, (_, i) => ({ cssVar: `--gray-${i+1}` })) },
  { name: 'Red',    dotVar: '--red-9',    steps: Array.from({length: 12}, (_, i) => ({ cssVar: `--red-${i+1}` })) },
  { name: 'Amber',  dotVar: '--amber-9',  steps: Array.from({length: 12}, (_, i) => ({ cssVar: `--amber-${i+1}` })) },
  { name: 'Blue',   dotVar: '--blue-9',   steps: Array.from({length: 12}, (_, i) => ({ cssVar: `--blue-${i+1}` })) },
  { name: 'Jade',   dotVar: '--jade-9',   steps: Array.from({length: 12}, (_, i) => ({ cssVar: `--jade-${i+1}` })) },
  { name: 'Violet', dotVar: '--violet-9', steps: Array.from({length: 12}, (_, i) => ({ cssVar: `--violet-${i+1}` })) },
] as const

const STEP_LEGEND = [
  { steps: '1–2',   label: 'Backgrounds', span: 2 },
  { steps: '3–5',   label: 'Interactive',  span: 3 },
  { steps: '6–8',   label: 'Borders',      span: 3 },
  { steps: '9–10',  label: 'Solid',        span: 2 },
  { steps: '11–12', label: 'Text',         span: 2 },
]

const ACCENT_GROUPS: TokenGroup[] = [
  {
    group: 'Accent — Primary',
    tokens: [
      { token: 'bg-accent-primary-fill',  cssVar: '--blue-4',  className: 'bg-accent-primary-fill',  step: 'blue-4',  desc: 'Primary accent background tint. Column 1 identity, decorative fills.' },
      { token: 'bg-accent-primary-solid', cssVar: '--blue-9',  className: 'bg-accent-primary-solid', step: 'blue-9',  desc: 'Primary accent solid. Dot indicators, badges.' },
      { token: 'text-accent-primary-fg',  cssVar: '--blue-11', className: 'bg-accent-primary-fill',  step: 'blue-11', desc: 'Primary accent text and icons.' },
    ],
  },
  {
    group: 'Accent — Secondary',
    tokens: [
      { token: 'bg-accent-secondary-fill',  cssVar: '--jade-4',  className: 'bg-accent-secondary-fill',  step: 'jade-4',  desc: 'Secondary accent background tint. Column 2 identity.' },
      { token: 'bg-accent-secondary-solid', cssVar: '--jade-9',  className: 'bg-accent-secondary-solid', step: 'jade-9',  desc: 'Secondary accent solid.' },
      { token: 'text-accent-secondary-fg',  cssVar: '--jade-11', className: 'bg-accent-secondary-fill',  step: 'jade-11', desc: 'Secondary accent text and icons.' },
    ],
  },
  {
    group: 'Accent — Tertiary',
    tokens: [
      { token: 'bg-accent-tertiary-fill',  cssVar: '--violet-4',  className: 'bg-accent-tertiary-fill',  step: 'violet-4',  desc: 'Tertiary accent background tint. Column 3 identity.' },
      { token: 'bg-accent-tertiary-solid', cssVar: '--violet-9',  className: 'bg-accent-tertiary-solid', step: 'violet-9',  desc: 'Tertiary accent solid.' },
      { token: 'text-accent-tertiary-fg',  cssVar: '--violet-11', className: 'bg-accent-tertiary-fill',  step: 'violet-11', desc: 'Tertiary accent text and icons.' },
    ],
  },
]

const NEUTRAL_GROUPS: TokenGroup[] = [
  {
    group: 'Backgrounds',
    tokens: [
      { token: 'bg-canvas',      cssVar: '--gray-1',  className: 'bg-canvas',      step: 'gray-1',  desc: 'Page background. The base layer everything sits on.' },
      { token: 'bg-surface',     cssVar: '--gray-2',  className: 'bg-surface',     step: 'gray-2',  desc: 'Elevated surfaces — cards, sidebars, info panels.' },
      { token: 'bg-shelf',       cssVar: '--gray-3',  className: 'bg-shelf',       step: 'gray-3',  desc: 'Side panel / shelf background. Defined, not yet in active use.' },
      { token: 'bg-fill',        cssVar: '--gray-3',  className: 'bg-fill',        step: 'gray-3',  desc: 'Subtle fills inside components — image placeholders, empty states.' },
      { token: 'bg-fill-hover',  cssVar: '--gray-4',  className: 'bg-fill-hover',  step: 'gray-4',  desc: 'Fill on hover — interactive rows and cards.' },
      { token: 'bg-fill-active', cssVar: '--gray-5',  className: 'bg-fill-active', step: 'gray-5',  desc: 'Pressed / active fill state. Defined, not yet in active use.' },
      { token: 'bg-solid',       cssVar: '--gray-9',  className: 'bg-solid',       step: 'gray-9',  desc: 'Solid controls — primary buttons, active indicators.' },
      { token: 'bg-solid-hover', cssVar: '--gray-10', className: 'bg-solid-hover', step: 'gray-10', desc: 'Solid control hover state.' },
    ],
  },
  {
    group: 'Text',
    tokens: [
      { token: 'text-fg',           cssVar: '--gray-12', className: 'bg-canvas', step: 'gray-12', desc: 'Primary text. Headlines, labels, body copy.' },
      { token: 'text-fg-secondary', cssVar: '--gray-11', className: 'bg-canvas', step: 'gray-11', desc: 'Secondary text. Metadata, captions, helper text.' },
    ],
  },
  {
    group: 'Borders',
    tokens: [
      { token: 'border-line-subtle', cssVar: '--gray-6', className: 'bg-canvas', step: 'gray-6', desc: 'Hairline and decorative dividers.' },
      { token: 'border-line',        cssVar: '--gray-7', className: 'bg-canvas', step: 'gray-7', desc: 'Default borders and dividers.' },
      { token: 'border-line-strong', cssVar: '--gray-8', className: 'bg-canvas', step: 'gray-8', desc: 'Emphasized borders — active selection, section dividers.' },
    ],
  },
]

const STATUS_GROUPS: TokenGroup[] = [
  {
    group: 'Status — Info',
    tokens: [
      { token: 'bg-info-fill',     cssVar: '--blue-2',  className: 'bg-info-fill', step: 'blue-2',  desc: 'Informational callout background. Assign mode, active column state.' },
      { token: 'border-info-line', cssVar: '--blue-6',  className: 'bg-canvas',    step: 'blue-6',  desc: 'Info callout border.' },
      { token: 'text-info-fg',     cssVar: '--blue-11', className: 'bg-info-fill', step: 'blue-11', desc: 'Info callout text and icons.' },
    ],
  },
  {
    group: 'Status — Warning',
    tokens: [
      { token: 'bg-warning-fill',     cssVar: '--amber-2',  className: 'bg-warning-fill', step: 'amber-2',  desc: 'Warning callout background. Caution notices, draft state.' },
      { token: 'border-warning-line', cssVar: '--amber-6',  className: 'bg-canvas',       step: 'amber-6',  desc: 'Warning callout border.' },
      { token: 'text-warning-fg',     cssVar: '--amber-11', className: 'bg-warning-fill', step: 'amber-11', desc: 'Warning callout text and icons.' },
    ],
  },
  {
    group: 'Status — Error',
    tokens: [
      { token: 'bg-error-fill',     cssVar: '--red-2',  className: 'bg-error-fill', step: 'red-2',  desc: 'Error background. Destructive actions, validation failures.' },
      { token: 'border-error-line', cssVar: '--red-6',  className: 'bg-canvas',     step: 'red-6',  desc: 'Error border.' },
      { token: 'text-error-fg',     cssVar: '--red-11', className: 'bg-error-fill', step: 'red-11', desc: 'Error text and icons.' },
    ],
  },
  {
    group: 'Status — Success',
    tokens: [
      { token: 'bg-success-fill',     cssVar: '--jade-2',  className: 'bg-success-fill', step: 'jade-2',  desc: 'Success callout background. Save confirmations, completed actions.' },
      { token: 'border-success-line', cssVar: '--jade-6',  className: 'bg-canvas',       step: 'jade-6',  desc: 'Success border.' },
      { token: 'text-success-fg',     cssVar: '--jade-11', className: 'bg-success-fill', step: 'jade-11', desc: 'Success text and icons.' },
    ],
  },
  {
    group: 'Tooltip',
    tokens: [
      { token: 'bg-tooltip-bg',  cssVar: '--gray-12', className: 'bg-tooltip-bg',  step: 'gray-12', desc: 'Inverted tooltip surface. Near-black in light mode, near-white in dark.' },
      { token: 'text-tooltip-fg', cssVar: '--gray-1',  className: 'bg-tooltip-bg', step: 'gray-1',  desc: 'Inverted tooltip text.' },
    ],
  },
]

export default function ColorTab() {
  return (
    <Tabs.Content value="color">

      {/* Intro */}
      <div className="mb-12 grid gap-8 lg:grid-cols-[1fr_1fr]">
        <div>
          <h2 className="font-zodiak text-2xl font-bold text-fg">Radix UI Colors</h2>
          <p className="mt-4 text-sm leading-7 text-fg-secondary">
            All colors in this UI are drawn from the Radix UI open-source color system —
            a set of perceptually-balanced scales designed specifically for interface work.
            Each scale runs from 1 (near-white or near-black) to 12 (high-contrast),
            with consistent semantic meaning across every scale: backgrounds at the low end,
            interactive fills and borders in the middle, solid controls at 9–10,
            and accessible text at 11–12.
          </p>
          <p className="mt-4 text-sm leading-7 text-fg-secondary">
            Dark mode is handled automatically — the same token names resolve to
            light or dark values depending on the active theme,
            with no <code className="rounded bg-fill px-1 py-0.5 font-mono text-xs">dark:</code> variants required.
            Use the toggle in the nav to switch modes and watch every swatch and token on this page respond in real time.
          </p>
        </div>
        <div className="border-l border-line pl-8">
          <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-fg-secondary">Shared Design Language</h3>
          <p className="mt-4 text-sm leading-7 text-fg-secondary">
            Because Radix Colors is a published, documented standard, it gives the development
            team and the customer's design team a common frame of reference. Rather than exchanging
            hex values or one-off Figma swatches, conversations about tone and feel can happen
            in terms of where something sits on a scale — how saturated, how light, how prominent.
            Semantic tokens then translate those decisions into consistent, reusable names
            that the UI applies everywhere automatically.
          </p>
          <p className="mt-4 text-sm leading-7 text-fg-secondary">
            The full documentation and interactive palette explorer are available at{' '}
            <span className="font-mono text-xs text-fg">radix-ui.com/colors</span>.
            The complete Radix palette includes over 30 scales — additional colors
            can be brought into the project upon request.
          </p>
        </div>
      </div>

      {/* Step semantics legend */}
      <div className="flex items-stretch mb-2">
        <div className="w-16 shrink-0" />
        {STEP_LEGEND.map(({ steps, label, span }) => (
          <div key={steps} style={{ flex: span }} className="border-r border-line last:border-r-0 py-2 px-1 text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-fg">{steps}</p>
            <p className="mt-0.5 text-[10px] text-fg-secondary">{label}</p>
          </div>
        ))}
      </div>

      {/* Scale rows */}
      <div className="space-y-3">
        <div className="flex items-center gap-1">
          <div className="w-16 shrink-0" />
          {Array.from({length: 12}, (_, i) => (
            <div key={i} className="flex-1 text-center text-[10px] font-bold text-fg-secondary">{i + 1}</div>
          ))}
        </div>
        {ALL_SCALES.map(({ name, dotVar, steps }) => (
          <div key={name} className="flex items-center gap-1">
            <div className="w-16 shrink-0 flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: `var(${dotVar})` }} />
              <span className="text-xs font-semibold text-fg-secondary">{name}</span>
            </div>
            {steps.map(({ cssVar }) => (
              <div
                key={cssVar}
                className="flex-1 h-10 rounded-sm border border-line/50"
                style={{ backgroundColor: `var(${cssVar})` }}
                title={cssVar}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Semantic tokens */}
      <section className="mt-16">
        <div className="mb-6 border-b border-line pb-2">
          <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-fg-secondary">Semantic Tokens</h2>
        </div>

        {/* Prose */}
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] mb-12">
          <div>
            <h3 className="font-zodiak text-xl font-bold text-fg">Adjusting the look and feel</h3>
            <p className="mt-4 text-sm leading-7 text-fg-secondary">
              While the Radix color scales give us a precise vocabulary for individual colors,
              semantic tokens are how we control the overall character of the UI.
              Rather than every component independently deciding which shade of gray to use,
              a small set of named roles — <span className="font-mono text-xs text-fg">bg-canvas</span>,{' '}
              <span className="font-mono text-xs text-fg">bg-surface</span>,{' '}
              <span className="font-mono text-xs text-fg">text-fg</span>, and so on — are defined once
              and used everywhere.
            </p>
            <p className="mt-4 text-sm leading-7 text-fg-secondary">
              This means a single change — remapping <span className="font-mono text-xs text-fg">bg-canvas</span>{' '}
              from gray-1 to, say, a warm off-white — propagates automatically across every
              screen at once. No hunting through component files.
            </p>
          </div>
          <div className="border-l border-line pl-8">
            <h3 className="font-zodiak text-xl font-bold text-fg">These are ours to define</h3>
            <p className="mt-4 text-sm leading-7 text-fg-secondary">
              Semantic tokens are not handed down by Radix or any third party — they are a design
              contract that we author and maintain. The current set is a starting point, not a ceiling.
              As the product evolves, new tokens can be introduced to handle emerging needs,
              existing tokens can be split when a single role turns out to cover too much ground,
              and mappings can be adjusted to shift the feel of the entire UI without touching
              a single component. If the design team decides the hover state should feel warmer,
              or that surfaces need more separation at certain zoom levels, those are token-level
              conversations — not per-component changes scattered across dozens of files.
            </p>
          </div>
        </div>

        {/* Token reference table */}
        <div className="border-t border-line pt-8">
          <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-fg-secondary mb-6">Token roles</h3>

          {/* Accent section */}
          <div className="mb-8 rounded border border-accent-primary-fill bg-accent-primary-fill/30 px-5 py-4">
            <p className="text-sm font-semibold text-fg">Theme accent colors</p>
            <p className="mt-2 text-sm leading-7 text-fg-secondary">
              The three accent slots — primary, secondary, and tertiary — are the single point of control
              for the product's color identity. They drive column labels, identity dots, badge fills, and
              any other decorative UI elements that carry hue. Remapping all three in{' '}
              <span className="font-mono text-xs text-fg">index.css</span> is all it takes to fully
              re-theme the application for a customer. The initial palette (blue · jade · violet) was
              chosen as a cool, editorial-neutral triad, but it is entirely fungible — a warm palette
              of rust, gold, and olive would require changing nine lines.
            </p>
          </div>
          <div className="mb-12">
            <TokenGroupTable groups={ACCENT_GROUPS} />
          </div>

          {/* Neutral / structural section */}
          <div className="mb-8 rounded border border-line-subtle bg-surface px-5 py-4">
            <p className="text-sm font-semibold text-fg">Backgrounds, text &amp; borders</p>
            <p className="mt-2 text-sm leading-7 text-fg-secondary">
              These tokens form the structural skeleton of the UI. All are mapped to the neutral gray
              scale, which is hue-free and resolves correctly in both light and dark mode without any
              additional overrides. Surfaces, fills, and border weights are drawn from a consistent
              set of Radix steps so that elevation and emphasis read reliably across themes.
            </p>
          </div>
          <div className="mb-12">
            <TokenGroupTable groups={NEUTRAL_GROUPS} />
          </div>

          {/* Status / intent section */}
          <div className="mb-8 rounded border border-warning-fill bg-warning-fill/40 px-5 py-4">
            <p className="text-sm font-semibold text-fg">Status &amp; intent colors</p>
            <p className="mt-2 text-sm leading-7 text-fg-secondary">
              Status tokens communicate system state without relying on position or text alone. Each
              state provides a fill for backgrounds, a line for borders, and an fg for text and
              icons — enough to build a complete callout or inline badge from a single semantic root.
              Info uses the primary accent blue at a subdued step, keeping it harmonious with column
              identity while remaining distinct. Warning and error pull from amber and red respectively,
              following established convention for caution and destructive action.
            </p>
          </div>
          <div className="mb-12">
            <TokenGroupTable groups={STATUS_GROUPS} />
          </div>
        </div>
      </section>

    </Tabs.Content>
  )
}
